import React, { useState, useCallback } from 'react';
import { FaCamera, FaTimes } from 'react-icons/fa';
import { cn } from '@/lib/utils';

interface ImageUploadProps {
  currentImage: string;
  onImageChange: (file: File) => void;
  className?: string;
  isProfile?: boolean;
}

const ImageUpload = ({ currentImage, onImageChange, className, isProfile }: ImageUploadProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      handleFileChange(file);
    }
  }, []);

  const handleFileChange = async (file: File) => {
    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      alert('File size too large. Please choose an image under 5MB.');
      return;
    }

    setIsLoading(true);
    try {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
        setIsLoading(false);
      };
      reader.readAsDataURL(file);
      onImageChange(file);
    } catch (error) {
      setIsLoading(false);
      console.error('Error handling image:', error);
    }
  };

  return (
    <div
      className={cn(
        "relative group",
        isProfile ? "w-32 h-32" : "w-full h-[300px]",
        className
      )}
      onDragEnter={(e) => {
        handleDrag(e);
        setIsDragging(true);
      }}
      onDragOver={handleDrag}
      onDragLeave={(e) => {
        handleDrag(e);
        setIsDragging(false);
      }}
      onDrop={handleDrop}
    >
      <img
        src={preview || currentImage}
        alt={isProfile ? "Profile" : "Cover"}
        className={cn(
          "object-cover transition-all duration-300",
          isProfile ? "w-32 h-32 rounded-full" : "w-full h-full",
          isDragging && "opacity-50 scale-95"
        )}
      />
      
      <label className={cn(
        "absolute cursor-pointer transition-all duration-200",
        isProfile ? (
          "bottom-0 right-0 p-2 rounded-full bg-primary text-primary-foreground"
        ) : (
          "bottom-4 right-4 px-4 py-2 rounded-md bg-background/80 hover:bg-background text-foreground backdrop-blur-sm"
        ),
        "opacity-0 group-hover:opacity-100 hover:scale-105"
      )}>
        <div className="flex items-center gap-2">
          <FaCamera className={cn(
            isProfile ? "w-4 h-4" : "w-5 h-5"
          )} />
          {!isProfile && <span>Change Cover</span>}
        </div>
        <input
          type="file"
          className="hidden"
          accept="image/*"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFileChange(file);
          }}
        />
      </label>

      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        </div>
      )}

      {isDragging && (
        <div className="absolute inset-0 border-2 border-dashed border-primary bg-primary/5 flex items-center justify-center">
          <p className="text-primary font-medium">Drop image here</p>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;